import { createFileRoute, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Loader2, Plus, RotateCw, Send, Trash2, Eye, RefreshCw, Copy } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { PageHeader } from "@/components/ui/page-header";
import { useScope } from "@/hooks/use-scope";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, dateRangeToBounds, retainDateRangeKeys } from "@/lib/date-range-search";

import {
  listEndpoints,
  upsertEndpoint,
  rotateEndpointSecret,
  deleteEndpoint,
  listDeliveries,
  listAttempts,
  replayDelivery,
  sendTestEvent,
} from "@/lib/webhooks.functions";

export const Route = createFileRoute("/_authenticated/app/admin/webhooks")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: WebhooksAdmin,
});

const KNOWN_EVENTS = [
  "order.status_changed",
  "order.held",
  "order.released",
  "order.batched",
  "order.shipped",
  "shipment.tracking_updated",
  "inventory.adjusted",
  "webhook.test",
] as const;

type EndpointRow = {
  id: string;
  name: string;
  description: string | null;
  url: string;
  events: string[];
  headers: Record<string, string>;
  is_enabled: boolean;
  consecutive_failures: number;
  last_success_at: string | null;
  last_failure_at: string | null;
  disabled_reason: string | null;
};

function WebhooksAdmin() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const urlSearch = Route.useSearch();
  type S = typeof urlSearch;
  const navigate = useNavigate({ from: Route.fullPath });
  const update = (patch: Partial<S>) => navigate({ search: (prev: S) => ({ ...prev, ...patch }) });
  const sort = urlSearch.sort ?? "desc";
  // Default last 24h for deliveries
  const effectiveStart = urlSearch.start
    ?? (urlSearch.end ? undefined : new Date(Date.now() - 86400000).toISOString().slice(0, 10));
  const { fromIso, toIso } = useMemo(
    () => dateRangeToBounds({ start: effectiveStart, end: urlSearch.end }),
    [effectiveStart, urlSearch.end],
  );

  const listEp = useServerFn(listEndpoints);
  const upsertEp = useServerFn(upsertEndpoint);
  const rotateEp = useServerFn(rotateEndpointSecret);
  const deleteEp = useServerFn(deleteEndpoint);
  const listDel = useServerFn(listDeliveries);
  const replayFn = useServerFn(replayDelivery);
  const sendTest = useServerFn(sendTestEvent);

  const endpointsQ = useQuery({
    queryKey: ["webhooks", "endpoints", tenantId],
    queryFn: () => listEp({ data: { tenantId: tenantId! } }),
    enabled: !!tenantId,
  });

  const deliveriesQ = useQuery({
    queryKey: ["webhooks", "deliveries", tenantId, fromIso, toIso, sort],
    queryFn: () => listDel({ data: { tenantId: tenantId!, limit: 100, fromIso, toIso, sort } }),
    enabled: !!tenantId,
    refetchInterval: 5_000,
  });

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<EndpointRow | null>(null);
  const [secretReveal, setSecretReveal] = useState<{ name: string; secret: string } | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);

  function openNew() {
    setEditing(null);
    setEditorOpen(true);
  }
  function openEdit(ep: EndpointRow) {
    setEditing(ep);
    setEditorOpen(true);
  }

  const saveMut = useMutation({
    mutationFn: (vars: {
      id: string | null;
      name: string;
      description: string;
      url: string;
      events: string[];
      headers: Record<string, string>;
      isEnabled: boolean;
    }) =>
      upsertEp({
        data: {
          id: vars.id,
          tenantId: tenantId!,
          name: vars.name,
          description: vars.description || null,
          url: vars.url,
          events: vars.events,
          headers: vars.headers,
          isEnabled: vars.isEnabled,
        },
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["webhooks", "endpoints", tenantId] });
      setEditorOpen(false);
      if (res.signingSecret) {
        setSecretReveal({ name: "Signing secret (shown once)", secret: res.signingSecret });
      } else {
        toast.success("Endpoint saved");
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rotateMut = useMutation({
    mutationFn: (id: string) => rotateEp({ data: { id } }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["webhooks", "endpoints", tenantId] });
      setSecretReveal({ name: "New signing secret (shown once)", secret: res.signingSecret });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteEp({ data: { id } }),
    onSuccess: () => {
      toast.success("Endpoint deleted");
      qc.invalidateQueries({ queryKey: ["webhooks", "endpoints", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMut = useMutation({
    mutationFn: (id: string) => sendTest({ data: { id, tenantId: tenantId! } }),
    onSuccess: () => {
      toast.success("Test event queued — dispatcher will deliver within ~60s");
      qc.invalidateQueries({ queryKey: ["webhooks", "deliveries", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const replayMut = useMutation({
    mutationFn: (outboxId: string) => replayFn({ data: { outboxId } }),
    onSuccess: () => {
      toast.success("Re-queued");
      qc.invalidateQueries({ queryKey: ["webhooks", "deliveries", tenantId] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (!tenantId) {
    return (
      <div className="p-6">
        <PageHeader title="Webhooks" description="Outbound HTTP delivery for order and inventory events." />
        <Card className="p-6">Select a tenant in the scope picker.</Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <PageHeader
        title="Webhooks"
        description="Subscribe external systems to events with HMAC-signed deliveries, retries, and dead-lettering."
        actions={
          <Button onClick={openNew} className="gap-2">
            <Plus className="h-4 w-4" /> New endpoint
          </Button>
        }
      />

      <Tabs defaultValue="endpoints">
        <TabsList>
          <TabsTrigger value="endpoints">Endpoints</TabsTrigger>
          <TabsTrigger value="deliveries">Recent deliveries</TabsTrigger>
        </TabsList>

        <TabsContent value="endpoints">
          <Card className="p-4">
            {endpointsQ.isLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (endpointsQ.data?.rows ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No endpoints yet. Create one to start receiving events.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-left text-xs text-muted-foreground">
                    <tr>
                      <th className="py-2">Name</th>
                      <th className="py-2">URL</th>
                      <th className="py-2">Events</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Last activity</th>
                      <th className="py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(endpointsQ.data!.rows as EndpointRow[]).map((ep) => (
                      <tr key={ep.id} className="border-t">
                        <td className="py-2 font-medium">{ep.name}</td>
                        <td className="py-2 font-mono text-xs truncate max-w-[280px]" title={ep.url}>
                          {ep.url}
                        </td>
                        <td className="py-2">
                          <div className="flex flex-wrap gap-1">
                            {ep.events.slice(0, 3).map((e) => (
                              <Badge key={e} variant="secondary" className="text-[10px]">{e}</Badge>
                            ))}
                            {ep.events.length > 3 && (
                              <Badge variant="outline" className="text-[10px]">+{ep.events.length - 3}</Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-2">
                          {ep.is_enabled ? (
                            <Badge variant="outline" className="text-[10px]">Enabled</Badge>
                          ) : (
                            <Badge variant="destructive" className="text-[10px]" title={ep.disabled_reason ?? ""}>
                              Disabled
                            </Badge>
                          )}
                          {ep.consecutive_failures > 0 && (
                            <div className="text-[10px] text-warning mt-1">
                              {ep.consecutive_failures} consecutive failures
                            </div>
                          )}
                        </td>
                        <td className="py-2 text-xs text-muted-foreground">
                          {ep.last_success_at
                            ? `OK ${new Date(ep.last_success_at).toLocaleString()}`
                            : ep.last_failure_at
                              ? `Fail ${new Date(ep.last_failure_at).toLocaleString()}`
                              : "—"}
                        </td>
                        <td className="py-2 text-right">
                          <div className="inline-flex gap-1">
                            <Button size="sm" variant="ghost" onClick={() => testMut.mutate(ep.id)} title="Send test event">
                              <Send className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => rotateMut.mutate(ep.id)} title="Rotate signing secret">
                              <RotateCw className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => openEdit(ep)} title="Edit">
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                if (confirm(`Delete endpoint "${ep.name}"?`)) deleteMut.mutate(ep.id);
                              }}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="deliveries">
          <Card className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2 flex-wrap">
                <DateRangeFilter
                  value={{ start: urlSearch.start, end: urlSearch.end }}
                  onChange={(next) => update({ start: next.start, end: next.end })}
                />
                <SortDirectionToggle
                  value={sort}
                  onChange={(s) => update({ sort: s === "desc" ? undefined : s })}
                />
                <span className="text-xs text-muted-foreground ml-1">Auto-refreshes every 5s.</span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => qc.invalidateQueries({ queryKey: ["webhooks", "deliveries", tenantId] })}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-1">Created</th>
                    <th className="py-1">Event</th>
                    <th className="py-1">Status</th>
                    <th className="py-1">Attempts</th>
                    <th className="py-1">Next attempt</th>
                    <th className="py-1">Last error</th>
                    <th className="py-1 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(deliveriesQ.data?.rows ?? []).map((d) => (
                    <tr key={d.id} className="border-t">
                      <td className="py-1">{new Date(d.created_at).toLocaleString()}</td>
                      <td className="py-1 font-mono">{d.event}</td>
                      <td className="py-1">
                        <Badge
                          variant={
                            d.status === "delivered"
                              ? "outline"
                              : d.status === "dead"
                                ? "destructive"
                                : d.status === "in_flight"
                                  ? "secondary"
                                  : "secondary"
                          }
                          className="text-[10px]"
                        >
                          {d.status}
                        </Badge>
                      </td>
                      <td className="py-1">{d.attempts}/{d.max_attempts}</td>
                      <td className="py-1">{d.next_attempt_at ? new Date(d.next_attempt_at).toLocaleTimeString() : "—"}</td>
                      <td className="py-1 text-muted-foreground truncate max-w-[220px]" title={d.last_error ?? ""}>
                        {d.last_error ?? "—"}
                      </td>
                      <td className="py-1 text-right">
                        <Button size="sm" variant="ghost" onClick={() => setViewing(d.id)} title="View attempts">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                        {(d.status === "dead" || d.status === "failed" || d.status === "delivered") && (
                          <Button size="sm" variant="ghost" onClick={() => replayMut.mutate(d.id)} title="Replay">
                            <RotateCw className="h-3.5 w-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {(deliveriesQ.data?.rows ?? []).length === 0 && (
                    <tr><td colSpan={7} className="py-6 text-center text-muted-foreground">No deliveries yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      <EndpointEditor
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        endpoint={editing}
        saving={saveMut.isPending}
        onSave={(v) => saveMut.mutate(v)}
      />

      <Dialog open={!!secretReveal} onOpenChange={() => setSecretReveal(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{secretReveal?.name}</DialogTitle>
            <DialogDescription>
              Copy this value now — it cannot be retrieved later. Use it on the receiving server to verify the
              <code className="mx-1">X-Webhook-Signature</code> header (HMAC-SHA256 of <code>timestamp.body</code>).
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2">
            <code className="flex-1 break-all bg-muted p-2 rounded text-xs">{secretReveal?.secret}</code>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                navigator.clipboard.writeText(secretReveal?.secret ?? "");
                toast.success("Copied");
              }}
            >
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <AttemptsDialog outboxId={viewing} onClose={() => setViewing(null)} listAttempts={listAttempts} />
    </div>
  );
}

function EndpointEditor({
  open,
  onClose,
  endpoint,
  saving,
  onSave,
}: {
  open: boolean;
  onClose: () => void;
  endpoint: EndpointRow | null;
  saving: boolean;
  onSave: (v: {
    id: string | null;
    name: string;
    description: string;
    url: string;
    events: string[];
    headers: Record<string, string>;
    isEnabled: boolean;
  }) => void;
}) {
  const [name, setName] = useState(endpoint?.name ?? "");
  const [description, setDescription] = useState(endpoint?.description ?? "");
  const [url, setUrl] = useState(endpoint?.url ?? "");
  const [events, setEvents] = useState<string[]>(endpoint?.events ?? []);
  const [headersText, setHeadersText] = useState(
    endpoint ? JSON.stringify(endpoint.headers ?? {}, null, 2) : "{}",
  );
  const [isEnabled, setIsEnabled] = useState(endpoint?.is_enabled ?? true);

  // Reset state when dialog opens with a different endpoint
  useMemo(() => {
    if (open) {
      setName(endpoint?.name ?? "");
      setDescription(endpoint?.description ?? "");
      setUrl(endpoint?.url ?? "");
      setEvents(endpoint?.events ?? []);
      setHeadersText(endpoint ? JSON.stringify(endpoint.headers ?? {}, null, 2) : "{}");
      setIsEnabled(endpoint?.is_enabled ?? true);
    }
  }, [open, endpoint]);

  function toggleEvent(e: string) {
    setEvents((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));
  }

  function submit() {
    let headers: Record<string, string> = {};
    try {
      const parsed = JSON.parse(headersText || "{}");
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        headers = parsed as Record<string, string>;
      } else {
        toast.error("Headers must be a JSON object");
        return;
      }
    } catch {
      toast.error("Headers must be valid JSON");
      return;
    }
    if (events.length === 0) {
      toast.error("Select at least one event");
      return;
    }
    onSave({
      id: endpoint?.id ?? null,
      name,
      description,
      url,
      events,
      headers,
      isEnabled,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{endpoint ? "Edit endpoint" : "New endpoint"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme order sync" />
            </div>
            <div className="flex items-end justify-end gap-2">
              <Label>Enabled</Label>
              <Switch checked={isEnabled} onCheckedChange={setIsEnabled} />
            </div>
          </div>
          <div>
            <Label>URL</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://example.com/webhooks/orders" />
          </div>
          <div>
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
          <div>
            <Label>Events</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {KNOWN_EVENTS.map((e) => (
                <button
                  key={e}
                  type="button"
                  onClick={() => toggleEvent(e)}
                  className={`text-[11px] px-2 py-1 rounded border ${
                    events.includes(e)
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label>Custom headers (JSON)</Label>
            <Textarea
              value={headersText}
              onChange={(e) => setHeadersText(e.target.value)}
              className="font-mono text-xs h-24"
              placeholder='{ "X-Source": "lovable" }'
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AttemptsDialog({
  outboxId,
  onClose,
  listAttempts: listAttemptsFn,
}: {
  outboxId: string | null;
  onClose: () => void;
  listAttempts: typeof listAttempts;
}) {
  const fn = useServerFn(listAttemptsFn);
  const q = useQuery({
    queryKey: ["webhooks", "attempts", outboxId],
    queryFn: () => fn({ data: { outboxId: outboxId!, limit: 20 } }),
    enabled: !!outboxId,
  });

  return (
    <Dialog open={!!outboxId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Delivery attempts</DialogTitle>
        </DialogHeader>
        {q.isLoading ? (
          <div className="flex items-center gap-2 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : (
          <table className="text-xs w-full">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">#</th>
                <th className="py-1">When</th>
                <th className="py-1">HTTP</th>
                <th className="py-1">Duration</th>
                <th className="py-1">Outcome</th>
                <th className="py-1">Error / response</th>
              </tr>
            </thead>
            <tbody>
              {(q.data?.rows ?? []).map((a) => (
                <tr key={a.id} className="border-t align-top">
                  <td className="py-1">{a.attempt_no}</td>
                  <td className="py-1">{new Date(a.at).toLocaleString()}</td>
                  <td className="py-1">{a.http_status ?? "—"}</td>
                  <td className="py-1">{a.duration_ms ?? "—"} ms</td>
                  <td className="py-1">
                    {a.succeeded ? <Badge variant="outline">OK</Badge> : <Badge variant="destructive">FAIL</Badge>}
                  </td>
                  <td className="py-1 font-mono break-all">{a.error || a.response_snippet || "—"}</td>
                </tr>
              ))}
              {(q.data?.rows ?? []).length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-muted-foreground">No attempts yet.</td></tr>
              )}
            </tbody>
          </table>
        )}
      </DialogContent>
    </Dialog>
  );
}